import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Environment, ENVIRONMENT_TOKEN } from 'account-hybrid/core/environment.service';

/**
 * Native-Angular port of the AngularJS `Advocates` service
 * (account-v1/src/core/api/advocates.js), People-relevant endpoints. The
 * URL and response shape are kept byte-identical so the parity harness's
 * recorded /api fixtures replay across baseline ↔ candidate.
 */
@Injectable()
export class AdvocatesApiService {
  constructor(
    private readonly http: HttpClient,
    @Inject(ENVIRONMENT_TOKEN) private readonly environment: Environment,
  ) {}

  /** GET /api/:prop/properties/:prop/advocates/:advocateUuid → details with client-side shaping. */
  details(propertyUuid: string, advocateUuid: string): Observable<any> {
    const url = `${this.environment.apiV1BaseUrl}api/${propertyUuid}/properties/${propertyUuid}/advocates/${advocateUuid}`;
    return this.http.get<any>(url).pipe(map((data) => transformDetailsResponse(data)));
  }

  /**
   * PUT save guest info — port of v1 `Advocates.save` (advocates.js): the guestInfo edit
   * modal's on-done. Payload: FirstName/LastName/Email/ProfileImageUrl/PersonUUID (+ StartDate
   * /EndDate when the guest has a stay). Same endpoint the v1 PUT hit.
   */
  save(propertyUuid: string, guestUUID: string, payload: any): Observable<any> {
    const url = `${this.environment.apiV1BaseUrl}api/${propertyUuid}/properties/${propertyUuid}/advocates/${guestUUID}`;
    return this.http.put<any>(url, payload);
  }

  /**
   * PUT remove/cancel guest — port of v1 `Advocates.delete` (advocates.js): the
   * cancelReservation modal's on-done. Same endpoint as save; body is PersonUUID + the reason
   * flags ({ DeleteReason, IsCancelled | IsTest }).
   */
  delete(propertyUuid: string, guestUUID: string, reason: any): Observable<any> {
    const url = `${this.environment.apiV1BaseUrl}api/${propertyUuid}/properties/${propertyUuid}/advocates/${guestUUID}`;
    return this.http.put<any>(url, { PersonUUID: guestUUID, ...reason });
  }

  /**
   * POST request a promotion-certificate verification — port of v1
   * `Advocates.requestPromotionCertificate` (advocates.js): companyCode + guestUUID as query
   * (v1 urlFormatter appended the model as the query string).
   */
  requestPromotionCertificate(propertyUuid: string, companyCode: string, guestUUID: string): Observable<any> {
    const url =
      `${this.environment.apiV1BaseUrl}api/${propertyUuid}/promoter-certificates/request-verification` +
      `?companyCode=${encodeURIComponent(companyCode)}&guestUUID=${encodeURIComponent(guestUUID)}`;
    return this.http.post<any>(url, {});
  }
}

/** Port of `transformDetailsResponse` from account-v1 advocates.js. */
function transformDetailsResponse(data: any): any {
  if (!data) return data;

  data.totalFollowers = intOrDefault(data.totalFollowers);
  data.totalVisitors = intOrDefault(data.totalVisitors);
  data.totalSignups = intOrDefault(data.totalSignups);

  // Most-recent activity type (drives the activityType label up top).
  let activityType: string | undefined;
  if (data.poststay && data.poststay.photo) {
    if (data.poststay.photo.shareDate) activityType = 'photo.share';
    else if (data.poststay.photo.voteDate) activityType = 'photo.vote';
    else if (data.poststay.photo.finalistDate) activityType = 'photo.finalist';
    else if (data.poststay.photo.createDate) activityType = 'photo.upload';
  } else if (data.poststay && data.poststay.quote) {
    if (data.poststay.quote.createDate) activityType = 'quote';
  } else if (data.prestay) {
    if (data.prestay.epochDate) activityType = 'prestay.share';
  }
  data.lastActivityType = activityType;

  if (data.prestay) {
    data.prestay.promotions = arrayOrDefault(data.prestay.promotions);
    for (const promo of data.prestay.promotions) promo.totalFollowers = intOrDefault(promo.totalFollowers);
    fixPromotions(data.prestay.promotions);

    data.prestay.incentives = arrayOrDefault(data.prestay.incentives);
    for (const inc of data.prestay.incentives) {
      inc.epochExpirationDate = intOrNull(inc.epochExpirationDate);
      inc.utcExpirationDate = inc.epochExpirationDate != null ? new Date(inc.epochExpirationDate) : null;
    }
  }

  if (data.poststay) {
    data.poststay.promotions = arrayOrDefault(data.poststay.promotions);
    for (const promo of data.poststay.promotions) promo.totalFollowers = intOrDefault(promo.totalFollowers);
    fixPromotions(data.poststay.promotions);

    data.poststay.incentives = arrayOrDefault(data.poststay.incentives);
    for (const inc of data.poststay.incentives) {
      inc.epochExpirationDate = intOrNull(inc.epochExpirationDate);
      inc.utcExpirationDate = inc.epochExpirationDate != null ? new Date(inc.epochExpirationDate) : null;
    }
  }

  if (data.photoContest) {
    data.photoContest.photos = arrayOrDefault(data.photoContest.photos);
    data.photoContest.emailsSent = arrayOrDefault(data.photoContest.emailsSent);
    for (const photo of data.photoContest.photos) {
      photo.promotions = arrayOrDefault(photo.promotions);
      photo.friendPromotions = arrayOrDefault(photo.friendPromotions);
      if (photo.winner) {
        photo.winner.epochIncentiveExpirationDate = intOrNull(photo.winner.epochIncentiveExpirationDate);
        photo.winner.utcExpirationDate =
          photo.winner.epochIncentiveExpirationDate != null ? new Date(photo.winner.epochIncentiveExpirationDate) : null;
      }
      fixPromotions(photo.promotions);
      fixPromotions(photo.friendPromotions);
    }
  }

  return data;
}

/** Port of fixPromotions — fold all non-fb/li/tw entries into an "Others" bucket. */
function fixPromotions(promotions: any[]): any[] {
  let othersTotal = 0;
  for (const p of promotions) {
    if (!['fb', 'li', 'tw'].includes(p.title)) othersTotal += p.totalFollowers ?? 0;
  }
  if (othersTotal !== 0) promotions.push({ title: 'Others', totalFollowers: othersTotal });
  return promotions;
}

/** Port of v1 parse.arrayOrDefault (parse.js) — the XML→JSON single-item case
 *  wraps a lone object into a one-element array instead of dropping it. */
function arrayOrDefault(v: any): any[] {
  if (!v) return [];
  if (Array.isArray(v)) return v;
  if (Array.isArray(v.item)) return v.item;
  if (Array.isArray(v.datum)) return v.datum;
  return [v];
}
function intOrDefault(v: any): number {
  const n = parseInt(v, 10);
  return Number.isFinite(n) ? n : 0;
}
function intOrNull(v: any): number | null {
  if (v == null) return null;
  const n = parseInt(v, 10);
  return Number.isFinite(n) ? n : null;
}
